import type { StackProps } from '@cmg/design-system';
import { Grid, Stack } from '@cmg/design-system';
import type { ComponentProps, ElementType, FormHTMLAttributes, ReactNode } from 'react';

import type { PageGridSectionSize } from './PageGridSection.model';
import { usePageGridSectionSizes } from './PageGridSection.model';
import { PageGridSectionHeader } from './PageGridSectionHeader';

type GridComponentProps = Omit<ComponentProps<typeof Grid>, 'component' | 'content'>;

export type PageGridSectionProps = Readonly<{
  /**
   * Size of the section used to determine how much space should section take on different screen sizes.
   * @default 'small'
   */
  size?: PageGridSectionSize;
  /**
   * Section title
   */
  title: string;
  /**
   * Specify when the grid section should be wrapper in Form, otherwise leave undefined
   */
  FormComponent?: ElementType<FormHTMLAttributes<HTMLFormElement>>;
  /**
   * Props to be passed to wrapping Form
   */
  formComponentProps?: Readonly<FormHTMLAttributes<HTMLFormElement>>;
  /**
   * Actions available in the section
   */
  actions?: ReactNode;
  /**
   * Error content
   */
  error?: ReactNode;
  /**
   * Content of the section
   */
  content?: ReactNode;
  slotProps?: { stack: StackProps };
}> &
  Readonly<GridComponentProps>;

export function PageGridSection({
  title,
  FormComponent,
  formComponentProps,
  actions,
  error,
  content,
  size = 'small',
  slotProps,
  ...gridProps
}: PageGridSectionProps): JSX.Element {
  const { xs, sm, md, lg, xl } = usePageGridSectionSizes(size);

  return (
    <Grid
      {...gridProps}
      item
      xs={xs}
      sm={sm}
      md={md}
      lg={lg}
      xl={xl}
      component={FormComponent ?? 'div'}
      {...formComponentProps}
    >
      <Stack rowGap={1} role="region" aria-label={title} {...slotProps?.stack}>
        <PageGridSectionHeader title={title} actions={actions} />
        {error}
        {content}
      </Stack>
    </Grid>
  );
}
